import { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, Package } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import axios, { API, getErrorMessage } from "@/lib/api";
import StatusBadge from "@/components/shared/StatusBadge";
import LoadingScreen from "@/components/shared/LoadingScreen";
import EmptyState from "@/components/shared/EmptyState";
import ConfirmDialog from "@/components/shared/ConfirmDialog";

const EMPTY = { name: "", type: "umrah", description: "", departure_date: "", duration_days: 9, quota: 40, price: 0, facilities: "", guide_id: "", status: "draft", registration_deadline: "", cover_image: "", airline: "", hotel: "" };

export default function AdminProgram() {
  const [items, setItems] = useState(null);
  const [guides, setGuides] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [deleteId, setDeleteId] = useState(null);

  const load = () => axios.get(`${API}/admin/program`).then(({ data }) => setItems(data)).catch(() => setItems([]));

  useEffect(() => {
    load();
    axios.get(`${API}/admin/pembimbing`).then(({ data }) => setGuides(data)).catch(() => {});
  }, []);

  const openCreate = () => { setEditing(null); setForm(EMPTY); setModalOpen(true); };
  const openEdit = (p) => {
    setEditing(p);
    setForm({ ...p, facilities: (p.facilities || []).join(", "), departure_date: p.departure_date?.slice(0, 10), registration_deadline: p.registration_deadline?.slice(0, 10) });
    setModalOpen(true);
  };

  const update = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  const handleSubmit = async () => {
    const payload = { ...form, facilities: form.facilities.split(",").map((s) => s.trim()).filter(Boolean), duration_days: Number(form.duration_days), quota: Number(form.quota), price: Number(form.price) };
    try {
      if (editing) {
        await axios.put(`${API}/admin/program/${editing.id}`, payload);
      } else {
        await axios.post(`${API}/admin/program`, payload);
      }
      toast.success("Program berhasil disimpan");
      setModalOpen(false);
      load();
    } catch (e) {
      toast.error(getErrorMessage(e));
    }
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${API}/admin/program/${deleteId}`);
      toast.success("Program berhasil dihapus");
      setDeleteId(null);
      load();
    } catch (e) {
      toast.error(getErrorMessage(e));
    }
  };

  return (
    <div data-testid="admin-program-page" className="space-y-5">
      <div className="flex flex-col items-start justify-between gap-3 sm:flex-row sm:items-center">
        <div>
          <h1 className="font-heading text-2xl font-bold text-[#1F2937]">Program Haji &amp; Umrah</h1>
          <p className="text-sm text-[#64748B]">Kelola daftar program yang ditawarkan kepada jamaah.</p>
        </div>
        <Button data-testid="admin-program-add-button" onClick={openCreate} className="gap-2 rounded-full bg-[#0F6B4F] text-white hover:bg-[#084C3A]">
          <Plus className="h-4 w-4" /> Tambah Program
        </Button>
      </div>

      {items === null ? <LoadingScreen /> : items.length === 0 ? (
        <EmptyState icon={Package} title="Belum ada program" description="Tambahkan program Haji atau Umrah pertama Anda." />
      ) : (
        <div className="overflow-x-auto rounded-xl border border-slate-100 bg-white">
          <Table data-testid="admin-program-table">
            <TableHeader>
              <TableRow><TableHead>Nama</TableHead><TableHead>Tipe</TableHead><TableHead>Keberangkatan</TableHead><TableHead>Kuota</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Aksi</TableHead></TableRow>
            </TableHeader>
            <TableBody>
              {items.map((p) => (
                <TableRow key={p.id} data-testid={`admin-program-row-${p.id}`}>
                  <TableCell className="font-medium text-[#1F2937]">{p.name}</TableCell>
                  <TableCell className="capitalize text-[#64748B]">{p.type}</TableCell>
                  <TableCell className="text-[#64748B]">{p.departure_date ? new Date(p.departure_date).toLocaleDateString("id-ID") : "-"}</TableCell>
                  <TableCell className="text-[#64748B]">{p.quota_taken}/{p.quota}</TableCell>
                  <TableCell><StatusBadge status={p.status} /></TableCell>
                  <TableCell className="text-right">
                    <Button variant="ghost" size="icon" data-testid={`admin-program-edit-${p.id}`} onClick={() => openEdit(p)}><Pencil className="h-4 w-4" /></Button>
                    <Button variant="ghost" size="icon" data-testid={`admin-program-delete-${p.id}`} onClick={() => setDeleteId(p.id)}><Trash2 className="h-4 w-4 text-red-500" /></Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-xl" data-testid="admin-program-modal">
          <DialogHeader><DialogTitle>{editing ? "Edit Program" : "Tambah Program"}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label>Nama Program</Label><Input data-testid="program-form-name" value={form.name} onChange={(e) => update("name", e.target.value)} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Tipe</Label>
                <Select value={form.type} onValueChange={(v) => update("type", v)}>
                  <SelectTrigger data-testid="program-form-type"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="haji">Haji</SelectItem><SelectItem value="umrah">Umrah</SelectItem></SelectContent>
                </Select>
              </div>
              <div><Label>Status</Label>
                <Select value={form.status} onValueChange={(v) => update("status", v)}>
                  <SelectTrigger data-testid="program-form-status"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="draft">Draf</SelectItem><SelectItem value="published">Dipublikasikan</SelectItem><SelectItem value="archived">Diarsipkan</SelectItem></SelectContent>
                </Select>
              </div>
            </div>
            <div><Label>Deskripsi</Label><Textarea data-testid="program-form-description" value={form.description} onChange={(e) => update("description", e.target.value)} rows={3} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Tanggal Keberangkatan</Label><Input data-testid="program-form-departure" type="date" value={form.departure_date} onChange={(e) => update("departure_date", e.target.value)} /></div>
              <div><Label>Batas Pendaftaran</Label><Input data-testid="program-form-deadline" type="date" value={form.registration_deadline} onChange={(e) => update("registration_deadline", e.target.value)} /></div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div><Label>Durasi (hari)</Label><Input data-testid="program-form-duration" type="number" value={form.duration_days} onChange={(e) => update("duration_days", e.target.value)} /></div>
              <div><Label>Kuota</Label><Input data-testid="program-form-quota" type="number" value={form.quota} onChange={(e) => update("quota", e.target.value)} /></div>
              <div><Label>Harga (Rp)</Label><Input data-testid="program-form-price" type="number" value={form.price} onChange={(e) => update("price", e.target.value)} /></div>
            </div>
            <div><Label>Fasilitas (pisahkan dengan koma)</Label><Textarea data-testid="program-form-facilities" value={form.facilities} onChange={(e) => update("facilities", e.target.value)} rows={2} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Maskapai</Label><Input data-testid="program-form-airline" value={form.airline} onChange={(e) => update("airline", e.target.value)} /></div>
              <div><Label>Hotel</Label><Input data-testid="program-form-hotel" value={form.hotel} onChange={(e) => update("hotel", e.target.value)} /></div>
            </div>
            <div><Label>URL Gambar Sampul</Label><Input data-testid="program-form-cover" value={form.cover_image} onChange={(e) => update("cover_image", e.target.value)} /></div>
            <div><Label>Pembimbing</Label>
              <Select value={form.guide_id} onValueChange={(v) => update("guide_id", v)}>
                <SelectTrigger data-testid="program-form-guide"><SelectValue placeholder="Pilih pembimbing" /></SelectTrigger>
                <SelectContent>{guides.map((g) => <SelectItem key={g.id} value={g.id}>{g.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <Button data-testid="admin-program-submit-button" onClick={handleSubmit} className="w-full rounded-full bg-[#0F6B4F] text-white hover:bg-[#084C3A]">Simpan Program</Button>
          </div>
        </DialogContent>
      </Dialog>

      <ConfirmDialog open={!!deleteId} onOpenChange={(v) => !v && setDeleteId(null)} title="Hapus Program?" description="Program yang dihapus tidak dapat dikembalikan." onConfirm={handleDelete} testId="admin-program-delete-confirm" />
    </div>
  );
}
